/*
 * Webkul Page Builder 2026 — Magento → Shopify migration family behaviour.
 *
 * Three progressive-enhancement widgets, each a no-op when its markup is absent:
 *   1. FAQ accordion   (.wkm-faq-item)
 *   2. Cost calculator (.wkm-calc)  — live weeks / band / manual-vs-AI, and the
 *      "Get fixed price" button, which opens the theme's contact modal pre-filled
 *
 * The logo marquee is pure CSS (@keyframes wkm-marquee); render.php duplicates
 * the track so the -50% translate loops seamlessly with no JS.
 *
 * The calculator maths mirror the reference component exactly (log-scaled score
 * → week band, then a manual estimate at ~1.7× to show the time saved).
 */
(function () {
	'use strict';

	/* ---------------- FAQ accordion ---------------- */
	function initFaq() {
		/* Rows built from wkpageb/mig-faq-item-2 child blocks carry no .is-open
		   of their own (a child block has no index), so the first row of each
		   auto list is opened here and the list marked .is-ready — which retires
		   the CSS fallback that was showing it. */
		[].slice.call(document.querySelectorAll('.wkm-faq-list.is-auto')).forEach(function (list) {
			var rows = [].slice.call(list.querySelectorAll('.wkm-faq-item'));
			if (!rows.length) {
				return;
			}
			var anyOpen = rows.some(function (row) {
				return row.classList.contains('is-open');
			});
			if (!anyOpen) {
				rows[0].classList.add('is-open');
				var first = rows[0].querySelector('.wkm-faq-q');
				if (first) {
					first.setAttribute('aria-expanded', 'true');
				}
			}
			list.classList.add('is-ready');
		});

		var items = [].slice.call(document.querySelectorAll('.wkm-faq-item'));
		items.forEach(function (item) {
			var btn = item.querySelector('.wkm-faq-q');
			if (!btn) {
				return;
			}
			btn.addEventListener('click', function () {
				var open = item.classList.toggle('is-open');
				btn.setAttribute('aria-expanded', open ? 'true' : 'false');
			});
		});
	}

	/* ---------------- Cost calculator ----------------
	   The cards are authored, so this cannot name them: each one carries HOW it
	   counts on its own input (data-scale / data-weight / data-cap / data-offset)
	   and the score is the sum of the terms. Adding a card adds a term.

	   MIG_FIELD_DEFAULTS is what the original five counted for, for a card saved
	   before the weights became editable — its input carries no data-weight, so
	   it is looked up by key. Mirrors src/blocks/_shared/mig-calc.js and
	   wkpageb_mig_field_weighting() in includes/mig-render-helpers.php; keep the
	   three in step. */
	var MIG_FIELD_DEFAULTS = {
		products: { scale: 'log', weight: 1.1, cap: 0, offset: 0 },
		customers: { scale: 'log', weight: 0.5, cap: 0, offset: 0 },
		orders: { scale: 'log', weight: 0.7, cap: 0, offset: 0 },
		plugins: { scale: 'linear', weight: 0.08, cap: 3, offset: 0 },
		languages: { scale: 'linear', weight: 0.35, cap: 2, offset: 1 },
	};
	var MIG_FIELD_FALLBACK = { scale: 'log', weight: 0.5, cap: 0, offset: 0 };

	function fieldWeighting(inp) {
		var scale = inp.getAttribute('data-scale') || '';
		var weight = parseFloat(inp.getAttribute('data-weight')) || 0;

		if (scale || weight) {
			return {
				scale: 'linear' === scale ? 'linear' : 'log',
				weight: weight,
				cap: parseFloat(inp.getAttribute('data-cap')) || 0,
				offset: parseFloat(inp.getAttribute('data-offset')) || 0,
			};
		}

		return MIG_FIELD_DEFAULTS[inp.getAttribute('data-key')] || MIG_FIELD_FALLBACK;
	}

	function calcResult(fields) {
		var log10 = function (n) {
			return Math.log(n) / Math.LN10;
		};

		var score = fields.reduce(function (sum, f) {
			var n = Math.max(0, (f.value || 0) - f.weighting.offset);
			if ('linear' === f.weighting.scale) {
				var raw = n * f.weighting.weight;
				return sum + (f.weighting.cap > 0 ? Math.min(f.weighting.cap, raw) : raw);
			}
			return sum + log10(n + 10) * f.weighting.weight;
		}, 0);

		var lo = Math.max(3, Math.round(score - 2.5));
		var hi = lo + (score > 9 ? 4 : score > 7 ? 3 : 1);
		var band = score > 9 ? 'Large' : score > 7 ? 'Medium' : 'Small';
		var mLo = Math.round(lo * 1.7 + 1);
		var mHi = Math.round(hi * 1.7 + 1);
		var saved = Math.round(((mLo - lo) + (mHi - hi)) / 2);

		return {
			band: band,
			weeks: lo + '–' + hi,
			manual: mLo + '–' + mHi,
			saved: saved,
		};
	}

	function initCalc(root) {
		var inputs = [].slice.call(root.querySelectorAll('.wkm-calc-input'));
		var presets = [].slice.call(root.querySelectorAll('.wkm-calc-preset'));
		var form = root.querySelector('.wkm-calc-form');
		var success = root.querySelector('.wkm-calc-success');
		var emailInput = root.querySelector('.wkm-calc-email');
		/* The submit control is a core/button now, which renders an <a> inside
		   a .wkm-calc-submit wrapper. The bare .wkm-calc-submit selector still
		   matches a panel saved before that move, which drew a <button>. */
		var submit = root.querySelector('.wkm-calc-submit .wp-block-button__link') || root.querySelector('.wkm-calc-submit');

		/* Keyed by data-key, never by position, so dragging the cards into a new
		   order in the editor cannot change what is computed. Each entry keeps
		   the card's weighting alongside its value. */
		var state = {};
		inputs.forEach(function (inp) {
			state[inp.getAttribute('data-key')] = {
				value: parseInt(inp.value, 10) || 0,
				weighting: fieldWeighting(inp),
			};
		});

		function fieldList() {
			return Object.keys(state).map(function (k) {
				return state[k];
			});
		}

		function out(name) {
			return root.querySelector('[data-out="' + name + '"]');
		}

		function syncPresets() {
			presets.forEach(function (btn) {
				var key = btn.getAttribute('data-key');
				var val = parseInt(btn.getAttribute('data-val'), 10);
				btn.classList.toggle('is-active', !!state[key] && state[key].value === val);
			});
		}

		function render() {
			var r = calcResult(fieldList());
			var b = out('band');
			var w = out('weeks');
			var m = out('manual');
			var s = out('saved');
			if (b) {
				/* calcResult returns the band as a key; the words are authored
				   per block and travel on the element as data-band-*, so a
				   custom label survives the first input change. */
				var bandWord = b.getAttribute('data-band-' + r.band.toLowerCase());
				b.textContent = null === bandWord ? r.band : bandWord;
			}
			if (w) {
				w.textContent = r.weeks;
			}
			if (m) {
				m.textContent = r.manual;
			}
			if (s) {
				s.textContent = '~' + r.saved;
			}
			syncPresets();
		}

		inputs.forEach(function (inp) {
			inp.addEventListener('input', function () {
				var entry = state[inp.getAttribute('data-key')];
				if (entry) {
					entry.value = Math.max(0, parseInt(inp.value, 10) || 0);
				}
				render();
			});
		});

		presets.forEach(function (btn) {
			btn.addEventListener('click', function () {
				var key = btn.getAttribute('data-key');
				var val = Math.max(0, parseInt(btn.getAttribute('data-val'), 10) || 0);
				if (state[key]) {
					state[key].value = val;
				}
				var field = root.querySelector('.wkm-calc-input[data-key="' + key + '"]');
				if (field) {
					field.value = val;
				}
				render();
			});
		});

		/* What the visitor entered, as label → value, in the order the cards are
		   shown. The label is the card's own <label>, so authored names carry. */
		function enteredFields() {
			return inputs.map(function (inp) {
				var label = inp.parentElement ? inp.parentElement.querySelector('label') : null;
				return {
					label: label ? label.textContent.trim() : inp.getAttribute('data-key'),
					value: inp.value,
				};
			});
		}

		function textOf(name) {
			var el = out(name);
			return el ? el.textContent.trim() : '';
		}

		/* Subject and enquiry for the contact form, built from the live result. */
		function quoteMessage() {
			var bandEl = out('band');
			var band = bandEl && bandEl.parentElement ? bandEl.parentElement.textContent.trim() : '';
			var weeksEl = out('weeks');
			var unit = weeksEl && weeksEl.nextElementSibling ? weeksEl.nextElementSibling.textContent.trim() : 'wks';
			var heading = document.querySelector('h1');
			var page = heading ? heading.textContent.replace(/\s+/g, ' ').trim() : document.title;

			/* Page first: the enquiry box shows only a few rows, and the page is
			   what tells sales which migration this request is about. */
			var lines = ['Fixed price quote request from the migration cost calculator.', ''];
			lines.push('Page details:');
			if (page) {
				lines.push('- Page: ' + page);
			}
			lines.push('- URL: ' + window.location.origin + window.location.pathname);
			lines.push('');
			lines.push('Store details:');
			enteredFields().forEach(function (f) {
				lines.push('- ' + f.label + ': ' + f.value);
			});
			lines.push('');
			if (band) {
				lines.push('Store size: ' + band);
			}
			lines.push('Estimated timeline: ' + textOf('weeks') + ' ' + unit + ' (AI-assisted), ' + textOf('manual') + ' ' + unit + ' manual');

			return {
				subject: ('Fixed price quote' + (page ? ' – ' + page : '') + (band ? ' (' + band + ')' : '')).slice(0, 400),
				message: lines.join('\n').slice(0, 2000),
			};
		}

		if (submit && form) {
			if (emailInput) {
				emailInput.required = true;
				/* Enter in the email field asks for the quote, as a form would. */
				emailInput.addEventListener('keydown', function (event) {
					if ('Enter' === event.key) {
						event.preventDefault();
						submit.click();
					}
				});
			}

			submit.addEventListener('click', function (event) {
				/* A core/button is an <a>; this one opens the quote form rather
				   than navigating. */
				event.preventDefault();
				var email = emailInput ? emailInput.value.trim() : '';

				if (emailInput && (!email || !emailInput.checkValidity())) {
					emailInput.reportValidity();
					emailInput.focus();
					return;
				}

				var quote = quoteMessage();
				if (openContactModal({ email: email, subject: quote.subject, message: quote.message })) {
					return;
				}

				// No contact modal on this page: keep the inline confirmation.
				var shown = out('email');
				if (shown) {
					shown.textContent = email || 'your inbox';
				}
				form.style.display = 'none';
				if (success) {
					success.style.display = 'flex';
				}
			});
		}

		render();
	}

	/* ---------------- Contact modal ----------------
	   The theme's "Talk to Sales" modal (#contact-modal, a Contact Form 7 form)
	   opens for any click on a[href="#contact"]. The calculator fills that form
	   and opens it through the same trigger, so the visitor sends the quote
	   request from the one form the site already handles. Fields are matched by
	   CF7 name first, then by type, so a renamed field still gets filled.

	   Returns false when there is no modal or form to use. */
	function setFieldValue(field, value) {
		if (!field) {
			return;
		}
		field.value = value;
		/* `input` only: CF7 validates the whole form on `change`, which put a
		   "Please fill out this field." under the still-empty Name field. */
		field.dispatchEvent(new Event('input', { bubbles: true }));
	}

	function openContactModal(data) {
		var modal = document.getElementById('contact-modal');
		if (!modal) {
			return false;
		}
		var scope = modal.querySelector('[data-modal-type="#contact"]') || modal;
		var cf7 = scope.querySelector('form.wpcf7-form');
		if (!cf7) {
			return false;
		}

		setFieldValue(cf7.querySelector('[name="your-email"]') || cf7.querySelector('input[type="email"]'), data.email);
		setFieldValue(cf7.querySelector('[name="your-subject"]') || cf7.querySelector('input[name*="subject"]'), data.subject);
		setFieldValue(cf7.querySelector('textarea[name="your-message"]') || cf7.querySelector('textarea'), data.message);

		var trigger = document.createElement('a');
		trigger.href = '#contact';
		trigger.hidden = true;
		document.body.appendChild(trigger);
		trigger.click();
		document.body.removeChild(trigger);

		if ('on' !== modal.getAttribute('modal')) {
			return false;
		}

		var name = cf7.querySelector('[name="your-name"]');
		if (name && !name.value) {
			name.focus({ preventScroll: true });
		}
		return true;
	}

	function initCalcs() {
		[].slice.call(document.querySelectorAll('.wkm-calc')).forEach(initCalc);
	}

	function boot() {
		initFaq();
		initCalcs();
	}

	if (document.readyState !== 'loading') {
		boot();
	} else {
		document.addEventListener('DOMContentLoaded', boot);
	}
})();
